<template>
  <div :class="stacked ? 'flex flex-col gap-2.5' : 'grid gap-3 sm:grid-cols-2 xl:grid-cols-4'">
    <component
      :is="card.to ? 'router-link' : 'button'"
      v-for="card in cards"
      :key="card.key"
      data-slot="create-rule-card"
      v-bind="card.to ? { to: card.to } : { type: 'button' }"
      :aria-disabled="card.disabled || null"
      :class="[
        'group relative isolate flex flex-col gap-2 overflow-hidden rounded-xl p-4 pr-14 text-left no-underline',
        GLOW_TRANSITION,
        card.isAi ? 'bg-primary' : 'glass-tile',
        card.disabled ? 'cursor-not-allowed opacity-70' : [ 'cursor-pointer', card.glow ],
      ]"
      @click="() => onClickCard(card)"
    >
      <!-- Spins only on hover: a constant animation would repaint every frame
           and, with the page's backdrop-filter glass, keep the GPU busy. -->
      <span v-if="card.isAi" class="absolute -inset-1/4 -z-10 flex items-center">
        <span
          class="aspect-square w-full animate-spin-slow bg-[length:200%] bg-[position:50%_30%] [animation-play-state:paused] group-hover:[animation-play-state:running] motion-reduce:animate-none"
          :style="{ backgroundImage: 'url(' + toolsGradient + ')' }"
        />
      </span>

      <span class="flex items-center gap-2">
        <i :class="[ 'icon size-6 shrink-0', card.icon, card.iconColor ]" />
        <span :class="[ 'text-sm font-bold whitespace-nowrap', card.isAi ? 'text-white' : card.text ]">{{ card.title }}</span>
      </span>
      <span :class="[ 'text-sm', card.isAi ? 'text-white' : 'text-foreground' ]">{{ card.description }}</span>

      <span
        v-if="!card.disabled"
        aria-hidden="true"
        :class="[ 'glass-button absolute right-3.5 bottom-3.5 flex size-8 items-center justify-center rounded-full', card.isAi ? 'text-primary' : card.text ]"
      >
        <chevron-right-icon class="size-4" />
      </span>
    </component>
  </div>
</template>

<script>
import { ChevronRight } from '@lucide/vue';

import toolsGradient from '@/assets/img/tools-gradient.png';
import { RULE_ACTION } from '@/consts.js';
import OnboardingAd from '@/ui/components/OnboardingAd.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';

/**
 * Entry cards for creating an automation (AI mode and the three types).
 * Without connected assets every card opens the onboarding instead.
 */
export default {
  name: 'CreateNewRuleCards',
  components: {
    ChevronRightIcon: ChevronRight,
  },
  props: {
    // One column (for the mobile sheet).
    stacked: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: [ 'select' ],
  data: () => ({
    GLOW_TRANSITION,
    toolsGradient,
  }),
  computed: {
    allTypesOfAssetsConnected() {
      return this.$root.currentCompany.connected_profiles.length !== 0
        && this.$root.currentCompany.ad_accounts.length !== 0
        && this.$root.currentCompany.source_profiles.length !== 0;
    },
    hasAnyRules() {
      return this.$store.getters['rules/hasAnyRules'];
    },
    cards() {
      const cards = [
        {
          key: 'ai',
          icon: 'magic-stars',
          iconColor: 'bg-white',
          title: this.tr('Ai Ad Creation'),
          description: this.tr('Streamlined automation creation, powered by AI to minimize manual work.'),
          path: '/automation/new/ai-mode',
          isAi: true,
        },
        {
          key: RULE_ACTION.START,
          title: this.tr('Ad Creation'),
          description: this.tr('Continuously create ads from your posts, based on your settings, reducing manual work.'),
          path: '/automation/new/start',
        },
        {
          key: RULE_ACTION.SCALING_UP,
          title: this.tr('Ad Scaling'),
          description: this.tr('Scale your ads to maximize results, following your custom rules and settings, ensuring optimal reach.'),
          path: '/automation/new/scaling_up',
          disabled: !this.hasAnyRules,
        },
        {
          key: RULE_ACTION.STOP,
          title: this.tr('Ad Stop'),
          description: this.tr('Automatically stop ads based on your conditions, optimizing ad spend and campaign performance.'),
          path: '/automation/new/stop',
          disabled: !this.hasAnyRules,
        },
      ];

      return cards.map(card => {
        const style = card.isAi ? {} : ruleActionStyle(card.key);

        return {
          icon: style.icon,
          iconColor: style.bg,
          text: style.text,
          glow: card.isAi ? 'shadow-[0_0_20px_0_rgb(152_0_188/0)] hover:shadow-[0_0_20px_0_rgb(152_0_188/0.4)]' : style.glow,
          ...card,
          to: this.allTypesOfAssetsConnected && !card.disabled ? card.path : null,
        };
      });
    },
  },
  methods: {
    onClickCard(card) {
      if (!this.allTypesOfAssetsConnected) {
        this.$fullscreenModal.open(
          this,
          OnboardingAd,
          {},
          { closable: false },
        );
      }
      else if (!card.disabled) {
        this.$emit('select');
      }
    },
  },
};
</script>
